import { FC, FormEvent, ReactElement, useRef, useState } from 'react'
import classnames from 'classnames'
import { LocalMessage } from 'stream-chat'
import { renderText, useChannelStateContext, useMessageContext } from 'stream-chat-react'

import { Avatar, Button } from '@/components'
import { ConfirmModal, ImageAttachmentPreview, Popover, PopoverHandle } from '@/compositions'
import { useEditingMessage } from '@/context/EditingMessageContext'
import { getTimeString } from '@/helpers/date'
import { isSingleEmoji } from '@/helpers/emoji'
import { getInitials } from '@/helpers/name'
import { trans } from '@/helpers/trans'
import { useLongPress } from '@/hooks/useLongPress'

import './ConversationMessage.scss'

// Group messages from the same sender if they are sent within this time window
const GROUPING_WINDOW_MS = 5 * 60 * 1000

const isGroupedWithPrevious = (message: LocalMessage, previousMessage?: LocalMessage): boolean => {
    if (! previousMessage || previousMessage.deleted_at) {
        return false
    }

    if (previousMessage.user?.id !== message.user?.id) {
        return false
    }

    if (! previousMessage.created_at || ! message.created_at) {
        return false
    }

    const delta = new Date(message.created_at).getTime() - new Date(previousMessage.created_at).getTime()

    return delta <= GROUPING_WINDOW_MS
}

// Both are rendered as real <a> elements (see renderText()'s Anchor and the link-preview card
// below), so .closest() on either class resolves straight to the href we need.
const LINK_SELECTOR = 'a.str-chat__message-url-link, a.conversation-message__link-preview'

const findLinkHref = (target: EventTarget | null): string | undefined => {
    if (! (target instanceof Element)) {
        return undefined
    }

    const link = target.closest(LINK_SELECTOR)

    return link instanceof HTMLAnchorElement ? link.href : undefined
}

const formatTime = (value?: Date | string): string => {
    if (! value) {
        return ''
    }

    return getTimeString(value instanceof Date ? value : new Date(value))
}

export const ConversationMessage: FC = (): ReactElement | null => {
    const { getMessageActions, handleDelete, isMyMessage, message } = useMessageContext('ConversationMessage')
    const { messages } = useChannelStateContext('ConversationMessage')
    const { editingMessage, setEditingMessage } = useEditingMessage()

    const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)
    const [activeLinkHref, setActiveLinkHref] = useState<string | undefined>(undefined)
    const popoverRef = useRef<PopoverHandle>(null)

    const own = isMyMessage()
    const actions = getMessageActions()
    const canEdit = own && actions.includes('edit')
    const canDelete = own && actions.includes('delete')
    const isBeingEdited = editingMessage?.id === message.id
    const messageIndex = messages?.findIndex(item => item.id === message.id) ?? -1
    const previousMessage = messageIndex > 0 ? messages?.[messageIndex - 1] : undefined
    const nextMessage = messageIndex >= 0 ? messages?.[messageIndex + 1] : undefined
    // A group shows the sender's name on its first message and the time on its last
    const isFirstOfGroup = ! isGroupedWithPrevious(message, previousMessage)
    const isLastOfGroup = ! nextMessage || ! isGroupedWithPrevious(nextMessage, message)
    const isEdited = !! message.message_text_updated_at

    const longPress = useLongPress(
        (target) => {
            setActiveLinkHref(findLinkHref(target))
            popoverRef.current?.open()
        },
        (target) => canEdit || canDelete || !! findLinkHref(target),
    )

    const imageUrls = (message.attachments ?? [])
        .flatMap(attachment => (
            attachment.type === 'image' && attachment.image_url && ! attachment.og_scrape_url ? [attachment.image_url] : []
        ))
    const linkPreviews = (message.attachments ?? []).filter(attachment => !! attachment.og_scrape_url)
    const isDeleted = message.type === 'deleted' || !! message.deleted_at

    if (! isDeleted && ! message.text && ! imageUrls.length && ! linkPreviews.length) {
        return null
    }

    const senderName = message.user?.name
    // Only the first message of a group shows the avatar, the others keep its space to stay aligned
    const senderAvatar = ! own && (isFirstOfGroup ? (
        <Avatar
            className="conversation-message__avatar"
            image={message.user?.image}
            initials={getInitials(senderName)}
            name={senderName}
        />
    ) : (
        <span className="conversation-message__avatar-spacer" />
    ))
    const messageClassName = classnames('conversation-message', {
        'conversation-message--own': own,
        'conversation-message--editing': isBeingEdited,
    })

    if (isDeleted) {
        return (
            <div className={messageClassName}>
                {senderAvatar}
                <div className="conversation-message__content">
                    {! own && senderName && isFirstOfGroup && (
                        <strong className="conversation-message__name">{senderName}</strong>
                    )}
                    <div className="conversation-message__bubble conversation-message__bubble--deleted">
                        {trans('pages.messages.messageDeleted')}
                    </div>
                    {isLastOfGroup && (
                        <div className="conversation-message__meta">
                            <time className="conversation-message__time">
                                {formatTime(message.created_at)}
                            </time>
                        </div>
                    )}
                </div>
            </div>
        )
    }

    const handleEditClick = (): void => {
        popoverRef.current?.close()
        setEditingMessage(message)
    }

    const handleDeleteClick = (): void => {
        popoverRef.current?.close()
        setIsConfirmingDelete(true)
    }

    const confirmDelete = (event: FormEvent): void => {
        setIsConfirmingDelete(false)
        handleDelete(event)
    }

    const handleOpenLinkClick = (): void => {
        popoverRef.current?.close()

        if (activeLinkHref) {
            window.open(activeLinkHref, '_blank', 'noopener,noreferrer')
        }
    }

    return (
        <div className={messageClassName}>
            {senderAvatar}
            <Popover
                ref={popoverRef}
                label={trans('pages.messages.messageActions')}
                align={own ? 'end' : 'start'}
                mode="manual"
                className="conversation-message__body"
                contentClassName="conversation-message__actions-panel"
                renderTrigger={(): ReactElement => (
                    <div
                        className="conversation-message__content"
                        {...longPress}
                    >
                        {! own && senderName && isFirstOfGroup && (
                            <strong className="conversation-message__name">{senderName}</strong>
                        )}
                        {!! imageUrls.length && (
                            <ImageAttachmentPreview images={imageUrls} />
                        )}
                        {!! message.text && (
                            <div
                                className={classnames('conversation-message__bubble', {
                                    'conversation-message__bubble--jumbo-emoji': isSingleEmoji(message.text),
                                })}
                            >
                                {renderText(message.text, message.mentioned_users)}
                            </div>
                        )}
                        {linkPreviews.map(linkPreview => {
                            const previewImage = linkPreview.thumb_url || linkPreview.image_url

                            return (
                                <a
                                    key={linkPreview.og_scrape_url}
                                    href={linkPreview.title_link || linkPreview.og_scrape_url}
                                    target="_blank"
                                    rel="nofollow noreferrer noopener"
                                    className="conversation-message__link-preview"
                                >
                                    {!! previewImage && (
                                        <img
                                            src={previewImage}
                                            alt=""
                                            className="conversation-message__link-preview-image"
                                        />
                                    )}
                                    <span className="conversation-message__link-preview-content">
                                        <strong className="conversation-message__link-preview-title">
                                            {linkPreview.title || linkPreview.og_scrape_url}
                                        </strong>
                                        {!! linkPreview.text && (
                                            <span className="conversation-message__link-preview-text">
                                                {linkPreview.text}
                                            </span>
                                        )}
                                    </span>
                                </a>
                            )
                        })}
                        {(isLastOfGroup || isEdited) && (
                            <div className="conversation-message__meta">
                                {isLastOfGroup && (
                                    <time className="conversation-message__time">
                                        {formatTime(message.created_at)}
                                    </time>
                                )}
                                {isEdited && (
                                    <span className="conversation-message__edited">
                                        {trans('pages.messages.edited')}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                )}
            >
                {!! activeLinkHref && (
                    <Button
                        text={trans('pages.messages.openLink')}
                        className="conversation-message__action"
                        onClick={handleOpenLinkClick}
                    />
                )}
                {canEdit && (
                    <Button
                        text={trans('common.edit')}
                        className="conversation-message__action"
                        onClick={handleEditClick}
                    />
                )}
                {canDelete && (
                    <Button
                        text={trans('common.delete')}
                        className="conversation-message__action conversation-message__action--delete"
                        onClick={handleDeleteClick}
                    />
                )}
            </Popover>

            {isConfirmingDelete && (
                <ConfirmModal
                    title={trans('pages.messages.deleteMessage')}
                    message={trans('pages.messages.deleteMessageConfirm')}
                    confirmText={trans('common.delete')}
                    cancelText={trans('common.cancel')}
                    onConfirm={confirmDelete}
                    onCancel={() => setIsConfirmingDelete(false)}
                />
            )}
        </div>
    )
}
